import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import { formatUSDate } from "@/lib/date-utils"
import {
  caseExpensePath,
  casePath,
  caseReportPath,
  caseTimeEntryPath,
  editCaseExpensePath,
  editCaseTimeEntryPath,
  receiptCaseExpensePath,
  submitCaseExpensePath,
  submitCaseTimeEntryPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface TimeEntry {
  id: number
  case_name: string | null
  case_id: number | null
  case_status: number | null
  service_item: string | null
  started_at: string | null
  ended_at: string | null
  admin_minutes: number
  travel_hours: number
  mileage: number
  notes: string | null
  approval_notes: string | null
}

interface Expense {
  id: number
  case_name: string | null
  case_id: number | null
  case_status: number | null
  expense_date: string | null
  category: string | null
  expense_type_ref_id: number | null
  paid_to: string | null
  amount: number | null
  paid_by: string | null
  preapproved: boolean | null
  other_type: string | null
  per_diem_breakfast: number
  per_diem_lunch: number
  per_diem_dinner: number
  notes: string | null
  has_receipt: boolean
  receipt_type: string | null
  approval_notes: string | null
}

interface IncompleteReport {
  id: number
  case_id: number | null
  report_date: string | null
  case_name: string | null
  leader_name: string | null
}

interface Props {
  employee: { id: number; name: string }
  incomplete_reports: IncompleteReport[]
  time_entries: TimeEntry[]
  expenses: Expense[]
}

/* ── Helpers ────────────────────────────────────────────── */

function formatShortDate(dateStr: string | null): string {
  return formatUSDate(dateStr)
}

function formatTime(isoStr: string | null): string {
  if (!isoStr) return ""
  const d = new Date(isoStr)
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

function formatDateTime(isoStr: string): string {
  const d = new Date(isoStr)
  return (
    d.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    }) +
    " " +
    d.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  )
}

function isSameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString()
}

function formatHoursHM(hours: number): string {
  if (hours <= 0) return ""
  const totalMin = Math.round(hours * 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return `${h}:${m.toString().padStart(2, "0")}`
}

function formatMinutes(mins: number): string {
  if (mins <= 0) return ""
  const h = Math.floor(mins / 60)
  const m = mins % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function ReceiptIcon({ receiptType }: { receiptType: string | null }) {
  if (!receiptType) return null
  const lower = receiptType.toLowerCase()
  if (lower.includes("pdf")) {
    return (
      <span className="text-xs text-red-500" title="PDF receipt">
        PDF
      </span>
    )
  }
  if (
    lower.includes("image") ||
    lower.includes("jpg") ||
    lower.includes("jpeg") ||
    lower.includes("png")
  ) {
    return (
      <span className="text-xs text-blue-500" title="Image receipt">
        IMG
      </span>
    )
  }
  return (
    <span className="text-xs text-gray-400" title={receiptType}>
      File
    </span>
  )
}

/* ── Constants ──────────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [{ title: "Team", href: "/team" }]

/* ── Component ──────────────────────────────────────────── */

export default function Index({
  employee,
  incomplete_reports,
  time_entries,
  expenses,
}: Props) {
  const isEmpty =
    incomplete_reports.length === 0 &&
    time_entries.length === 0 &&
    expenses.length === 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Team - Pending Work" />

      <div className="p-6">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold">
            Pending Work - {employee.name}
          </h1>
        </div>

        {isEmpty ? (
          <div className="rounded border border-green-200 bg-green-50 px-6 py-8 text-center">
            <p className="text-lg font-medium text-green-800">All done!</p>
            <p className="mt-1 text-sm text-green-600">
              No incomplete reports, unsubmitted time entries, or expenses.
            </p>
          </div>
        ) : (
          <>
            {/* Incomplete Reports */}
            {incomplete_reports.length > 0 && (
              <div className="mb-6">
                <h2 className="mb-2 text-lg font-semibold">
                  Incomplete Reports ({incomplete_reports.length})
                </h2>
                <div className="border-hpi-light-gray overflow-hidden rounded border bg-white">
                  <div className="bg-hpi-light-gray grid grid-cols-[1fr_2fr] gap-4 px-4 py-2 text-sm font-medium md:grid-cols-[1fr_3fr_2fr]">
                    <div>Date</div>
                    <div>Case</div>
                    <div className="hidden md:block">Leader</div>
                  </div>
                  {incomplete_reports.map((report) => (
                    <Link
                      key={report.id}
                      href={
                        report.case_id
                          ? caseReportPath(report.case_id, report.id)
                          : "#"
                      }
                      className="text-hpi-dark-gray hover:bg-hpi-light-gray/50 grid grid-cols-[1fr_2fr] gap-4 border-t px-4 py-2 text-sm md:grid-cols-[1fr_3fr_2fr]"
                    >
                      <div>{formatShortDate(report.report_date)}</div>
                      <div>{report.case_name ?? "Unknown"}</div>
                      <div className="text-hpi-med-gray col-span-2 text-xs md:col-span-1 md:text-sm md:text-inherit">
                        {report.leader_name ?? ""}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Unsubmitted Time Entries */}
            {time_entries.length > 0 && (
              <div className="mb-6">
                <h2 className="mb-2 text-lg font-semibold">
                  Unsubmitted Time Entries ({time_entries.length})
                </h2>
                <ul className="space-y-2">
                  {time_entries.map((te) => (
                    <TimeEntryCard key={te.id} entry={te} />
                  ))}
                </ul>
              </div>
            )}

            {/* Unsubmitted Expenses */}
            {expenses.length > 0 && (
              <div className="mb-6">
                <h2 className="mb-2 text-lg font-semibold">
                  Unsubmitted Expenses ({expenses.length})
                </h2>
                <ul className="space-y-2">
                  {expenses.map((exp) => (
                    <ExpenseCard key={exp.id} expense={exp} />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

/* ── Time Entry Card ───────────────────────────────────── */

function RejectionBanner({ approvalNotes }: { approvalNotes: string | null }) {
  if (!approvalNotes?.includes("Declined")) return null
  const lines = approvalNotes.split("\n")
  const lastDecline = lines.find((l) => l.includes("Declined"))
  const reason = lastDecline?.replace(/^Declined\s*-\s*[^:]*:\s*/, "").trim()
  return (
    <div className="mt-2 rounded border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-800">
      <span className="font-medium">Previously rejected</span>
      {reason && reason !== lastDecline && <>: {reason}</>}
    </div>
  )
}

function TimeEntryCard({ entry }: { entry: TimeEntry }) {
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const dateStr = entry.started_at
    ? formatShortDate(entry.started_at.slice(0, 10))
    : ""

  const hasBlockTime = entry.admin_minutes > 0
  const hasTravel = entry.travel_hours > 0
  const hasMileage = entry.mileage > 0
  const canEditDelete = entry.case_id != null && (entry.case_status ?? 0) <= 1

  return (
    <li className="border-hpi-light-gray rounded border bg-white px-4 py-3">
      <div className="flex items-start justify-between">
        <div className="text-hpi-dark-gray text-sm font-medium">
          {entry.case_id ? (
            <Link
              href={casePath(entry.case_id)}
              className="text-hpi-blue hover:underline"
            >
              {entry.case_name ?? "No Case"}
            </Link>
          ) : (
            <span>{entry.case_name ?? "No Case"}</span>
          )}
          {dateStr && <span className="text-hpi-med-gray"> — {dateStr}</span>}
          {entry.service_item && (
            <span className="text-hpi-med-gray"> — {entry.service_item}</span>
          )}
        </div>
        {canEditDelete && (
          <div className="flex items-center gap-2">
            <Link
              href={editCaseTimeEntryPath(entry.case_id!, entry.id)}
              className="text-orange-500 hover:text-orange-600"
              title="Edit time entry"
            >
              <EditIcon />
            </Link>
            <button
              type="button"
              disabled={deleting}
              onClick={() => {
                if (
                  !confirm("Are you sure you want to delete this time entry?")
                )
                  return
                setDeleting(true)
                router.delete(caseTimeEntryPath(entry.case_id!, entry.id), {
                  onFinish: () => setDeleting(false),
                })
              }}
              className="text-red-500 hover:text-red-700 disabled:opacity-50"
              title="Delete time entry"
            >
              <TrashIcon />
            </button>
          </div>
        )}
      </div>

      <div className="text-hpi-med-gray mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {hasBlockTime ? (
          <span>Block: {formatMinutes(entry.admin_minutes)}</span>
        ) : (
          entry.started_at &&
          entry.ended_at && (
            <span>
              {isSameDay(entry.started_at, entry.ended_at)
                ? `${formatTime(entry.started_at)} to ${formatTime(entry.ended_at)}`
                : `${formatDateTime(entry.started_at)} to ${formatDateTime(entry.ended_at)}`}
            </span>
          )
        )}
        {hasTravel && <span>Travel: {formatHoursHM(entry.travel_hours)}</span>}
        {hasMileage && <span>{entry.mileage} miles</span>}
      </div>

      {entry.notes && (
        <div className="mt-1 text-sm">
          <span className="text-hpi-dark-gray font-medium">Explanation: </span>
          <span className="text-hpi-med-gray">{entry.notes}</span>
        </div>
      )}

      <RejectionBanner approvalNotes={entry.approval_notes} />

      {entry.case_id && (
        <div className="mt-2 text-center">
          <button
            type="button"
            disabled={submitting}
            onClick={() => {
              setSubmitting(true)
              router.patch(
                submitCaseTimeEntryPath(entry.case_id!, entry.id),
                {},
                {
                  onFinish: () => setSubmitting(false),
                },
              )
            }}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit for Approval"}
          </button>
        </div>
      )}
    </li>
  )
}

/* ── Expense Card ──────────────────────────────────────── */

function ExpenseCard({ expense }: { expense: Expense }) {
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const dateStr = expense.expense_date
    ? formatShortDate(expense.expense_date)
    : ""
  const isPerDiem = expense.expense_type_ref_id === 7
  const canEditDelete =
    expense.case_id != null && (expense.case_status ?? 0) <= 1

  return (
    <li className="border-hpi-light-gray rounded border bg-white px-4 py-3">
      <div className="flex items-start justify-between">
        <div className="text-hpi-dark-gray text-sm font-medium">
          {expense.case_id ? (
            <Link
              href={casePath(expense.case_id)}
              className="text-hpi-blue hover:underline"
            >
              {expense.case_name ?? "No Case"}
            </Link>
          ) : (
            <span>{expense.case_name ?? "No Case"}</span>
          )}
          {dateStr && <span className="text-hpi-med-gray"> — {dateStr}</span>}
          {expense.category && (
            <span className="text-hpi-med-gray">
              {" — "}
              {expense.category}
              {expense.expense_type_ref_id === 10 && expense.other_type && (
                <> ({expense.other_type})</>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {expense.has_receipt && expense.case_id && (
            <a
              href={receiptCaseExpensePath(expense.case_id, expense.id)}
              target="_blank"
              rel="noopener noreferrer"
              title="View receipt"
            >
              <ReceiptIcon receiptType={expense.receipt_type} />
            </a>
          )}
          {canEditDelete && (
            <>
              <Link
                href={editCaseExpensePath(expense.case_id!, expense.id)}
                className="text-orange-500 hover:text-orange-600"
                title="Edit expense"
              >
                <EditIcon />
              </Link>
              <button
                type="button"
                disabled={deleting}
                onClick={() => {
                  if (!confirm("Are you sure you want to delete this expense?"))
                    return
                  setDeleting(true)
                  router.delete(caseExpensePath(expense.case_id!, expense.id), {
                    onFinish: () => setDeleting(false),
                  })
                }}
                className="text-red-500 hover:text-red-700 disabled:opacity-50"
                title="Delete expense"
              >
                <TrashIcon />
              </button>
            </>
          )}
        </div>
      </div>

      {isPerDiem ? (
        <div className="text-hpi-med-gray mt-1 flex flex-wrap gap-x-4 text-sm">
          <PerDiemMeal label="Breakfast" amount={expense.per_diem_breakfast} />
          <PerDiemMeal label="Lunch" amount={expense.per_diem_lunch} />
          <PerDiemMeal label="Dinner" amount={expense.per_diem_dinner} />
        </div>
      ) : (
        <div className="text-hpi-med-gray mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {expense.paid_to && <span>Paid to: {expense.paid_to}</span>}
          {expense.amount != null && expense.amount > 0 && (
            <span>{formatCurrency(expense.amount)}</span>
          )}
          {expense.paid_by && <span>Payment: {expense.paid_by}</span>}
          {expense.preapproved && (
            <span className="text-green-600">Pre-Approved</span>
          )}
        </div>
      )}

      {expense.notes && (
        <div className="mt-1 text-sm">
          <span className="text-hpi-dark-gray font-medium">
            Explain Expense:{" "}
          </span>
          <span className="text-hpi-med-gray">{expense.notes}</span>
        </div>
      )}

      <RejectionBanner approvalNotes={expense.approval_notes} />

      {expense.case_id && (
        <div className="mt-2 text-center">
          <button
            type="button"
            disabled={submitting}
            onClick={() => {
              setSubmitting(true)
              router.patch(
                submitCaseExpensePath(expense.case_id!, expense.id),
                {},
                {
                  onFinish: () => setSubmitting(false),
                },
              )
            }}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit for Approval"}
          </button>
        </div>
      )}
    </li>
  )
}

/* ── Icons ─────────────────────────────────────────────── */

function EditIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
    >
      <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="h-4 w-4"
    >
      <path
        fillRule="evenodd"
        d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/* ── Per Diem Meal ─────────────────────────────────────── */

function PerDiemMeal({ label, amount }: { label: string; amount: number }) {
  const included = amount > 0
  return (
    <span className={included ? "text-green-600" : "text-hpi-med-gray"}>
      {included ? "✓" : "✗"} {label}
      {included && ` ${formatCurrency(amount)}`}
    </span>
  )
}
